/**
 * First-party analytics for the hosted storefront: the browser half of the event stream
 * (docs/analytics.md). Events come from the closed catalog in `@loot-os/api-contract`, queue in
 * memory, and go to `POST /v1/analytics/events` through the BFF proxy in batches, with the
 * shopper's session bearer when there is one so the server, never the browser, names the end
 * user. Nothing is sent until the store's analytics mode allows it: `enabled` sends, `disabled`
 * never does, and `consent_required` waits for Termly's `analytics` consent, which on a store
 * with no cookie banner never arrives (fail closed, by design). A per-browser anonymous id is
 * minted only once sending is allowed and is removed when consent is withdrawn.
 *
 * Nothing is recorded either until the page knows who the shopper is (`settleAnalyticsIdentity`):
 * the bearer arrives two requests after the config does, and a landing view recorded in between
 * would leave as an anonymous browser's, so every signed-in page load would count one shopper
 * twice.
 *
 * Delivery is best-effort: a batch the server refuses with 5xx or that never arrives is resent
 * once with the same ids (the rollups count distinct ids), then dropped. `fetch` with
 * `keepalive` is the one transport, because `sendBeacon` cannot carry the bearer.
 */
import {
  ANALYTICS_BATCH_MAX_EVENTS,
  ANALYTICS_EVENTS,
  ANALYTICS_MODES,
  type AnalyticsEventProperties,
  type AnalyticsMode,
  type BrowserAnalyticsEventName,
  DEFAULT_ANALYTICS_MODE,
  type StorefrontAnalytics,
} from "@loot-os/api-contract";
import { isLootApiErrorLike } from "@loot-os/react";
import type { AnyRouter } from "@tanstack/react-router";

import { setAnalyticsGateOpen } from "./analyticsGate.js";
import { LOOT_PROXY_BASE } from "./config.js";
import { lootClient } from "./queries.js";
import type { TenantConfig } from "./tenant.js";
import { onTermlyConsent } from "./termly.js";

/** Fail-closed read: a config cached by an older BFF has no `analytics`, and that means consent. */
export function analyticsOf(config: TenantConfig): StorefrontAnalytics {
  const block = (config as Partial<TenantConfig>).analytics as
    | Partial<StorefrontAnalytics>
    | undefined;
  const mode = block?.mode;
  return {
    mode:
      typeof mode === "string" && (ANALYTICS_MODES as readonly string[]).includes(mode)
        ? (mode as AnalyticsMode)
        : DEFAULT_ANALYTICS_MODE,
  };
}

/** A batch is sent once it holds this many events, or after `FLUSH_AFTER_MS`, whichever first. */
export const ANALYTICS_FLUSH_AT = 20;
export const ANALYTICS_FLUSH_AFTER_MS = 5000;
/** A batch that failed to arrive is resent once after this delay, with the same ids. */
const RESEND_AFTER_MS = 2000;
const ANON_KEY = "sf-analytics-anon";
const SESSION_KEY = "sf-analytics-session";
const ROOT_ROUTE_ID = "__root__";

interface QueuedEvent {
  id: string;
  name: BrowserAnalyticsEventName;
  occurredAt: number;
  properties: Record<string, unknown>;
}

interface Batch {
  token: string | null;
  events: QueuedEvent[];
}

let mode: AnalyticsMode = "disabled";
let consented = false;
let stopConsent: (() => void) | null = null;
/** Whether the page load's `/api/auth/me` → session mint has ended, signed in or not. */
let identitySettled = false;
let enabled = false;
let queue: QueuedEvent[] = [];
/** The bearer the queued events belong to; a change flushes them before the new one is used. */
let queueToken: string | null = null;
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let router: AnyRouter | null = null;
let listenersInstalled = false;
/** The last href a page view was sent for, so the consent-time view and the resolve never double. */
let lastPageViewHref: string | null = null;

function storage(kind: "local" | "session"): Storage | null {
  try {
    return kind === "local" ? window.localStorage : window.sessionStorage;
  } catch {
    return null;
  }
}

function readOrMint(kind: "local" | "session", key: string): string | null {
  const store = storage(kind);
  try {
    const existing = store?.getItem(key);
    if (existing) return existing;
    const minted = crypto.randomUUID();
    store?.setItem(key, minted);
    return minted;
  } catch {
    return null;
  }
}

function forget(kind: "local" | "session", key: string): void {
  try {
    storage(kind)?.removeItem(key);
  } catch {
    // storage unavailable: there was nothing to forget
  }
}

/**
 * Whether the mode and, under `consent_required`, the shopper's consent allow sending, and the
 * shopper's identity has settled so what is recorded leaves under the right bearer.
 */
function recompute(): void {
  const allowed = mode === "enabled" || (mode === "consent_required" && consented);
  const next = allowed && identitySettled;
  if (next === enabled) return;
  enabled = next;
  // The app's `/v1` calls say the same thing the tracker acts on (`analyticsGate.ts`).
  setAnalyticsGateOpen(enabled);
  if (enabled) {
    readOrMint("local", ANON_KEY);
    readOrMint("session", SESSION_KEY);
    trackCurrentPageView();
    return;
  }
  // Withdrawn or switched off: nothing queued may leave, and the browser is no longer known.
  queue = [];
  queueToken = null;
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  forget("local", ANON_KEY);
}

/**
 * Applies the store's analytics mode from its resolved config. Called once the fresh config has
 * answered, never from a cached one, so a store with a banner cannot send during the first
 * render. Idempotent: the consent subscription is made once and kept.
 */
export function configureAnalytics(analytics: StorefrontAnalytics): void {
  mode = analytics.mode;
  if (mode === "consent_required" && !stopConsent) {
    stopConsent = onTermlyConsent((state) => {
      consented = state.analytics;
      recompute();
    });
  }
  recompute();
}

/**
 * Tells the tracker the page load has learned who the shopper is: signed out, or signed in with
 * the session bearer minted (`ShopperProvider`). Until then nothing is recorded, and the landing
 * view is recorded here instead of when the config answered. Settles once and stays settled: a
 * later sign-out is a bearer change, which `track` already flushes around, and treating it as
 * unsettled would clear the queue and forget the browser the way withdrawn consent does.
 */
export function settleAnalyticsIdentity(): void {
  if (identitySettled) return;
  identitySettled = true;
  recompute();
}

/** Whether events are being recorded right now; the pages never need to ask, but tests do. */
export function isAnalyticsEnabled(): boolean {
  return enabled;
}

function scheduleFlush(): void {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    void flushAnalytics();
  }, ANALYTICS_FLUSH_AFTER_MS);
}

/**
 * Records one event. Dropped, not queued, while sending is not allowed: an event replayed after
 * consent would predate it. Typed against the catalog; in development the properties are
 * checked against the event's schema so a wrong key fails here rather than as a silent 400.
 */
export function track<N extends BrowserAnalyticsEventName>(
  name: N,
  properties: AnalyticsEventProperties<N>,
): void {
  if (!enabled) return;
  if (import.meta.env.DEV) {
    const result = ANALYTICS_EVENTS[name].properties.safeParse(properties);
    if (!result.success) {
      throw new Error(`analytics: ${name} properties are not what the catalog allows`);
    }
  }
  const token = lootClient.getSessionToken();
  if (queue.length > 0 && token !== queueToken) {
    // The shopper signed in or out mid-queue: what was queued belongs to the old identity.
    void flushAnalytics();
  }
  queueToken = token;
  queue.push({
    id: crypto.randomUUID(),
    name,
    occurredAt: Date.now(),
    properties: properties as Record<string, unknown>,
  });
  if (queue.length >= ANALYTICS_FLUSH_AT) void flushAnalytics();
  else scheduleFlush();
}

/** Sends whatever is queued now; the checkout redirect and sign-out call it before leaving. */
export async function flushAnalytics(): Promise<void> {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  if (queue.length === 0) return;
  const batch: Batch = { token: queueToken, events: queue.slice(0, ANALYTICS_BATCH_MAX_EVENTS) };
  queue = queue.slice(batch.events.length);
  if (queue.length > 0) scheduleFlush();
  await send(batch, 0);
}

async function send(batch: Batch, attempt: number): Promise<void> {
  let status: number;
  try {
    const res = await fetch(`${LOOT_PROXY_BASE}/v1/analytics/events`, {
      method: "POST",
      keepalive: true,
      headers: {
        "content-type": "application/json",
        accept: "application/json",
        ...(batch.token ? { authorization: `Bearer ${batch.token}` } : {}),
      },
      body: JSON.stringify({
        anonymousId: readOrMint("local", ANON_KEY) ?? undefined,
        sessionId: readOrMint("session", SESSION_KEY) ?? undefined,
        events: batch.events,
      }),
    });
    status = res.status;
  } catch {
    status = 0;
  }
  if (status >= 200 && status < 300) return;
  if (status === 403) {
    // The environment's mode is `disabled`: the config the page loaded with is stale.
    mode = "disabled";
    recompute();
    return;
  }
  if (status >= 400 && status < 500) return;
  if (attempt === 0) {
    await new Promise((resolve) => setTimeout(resolve, RESEND_AFTER_MS));
    if (enabled) await send(batch, 1);
  }
}

function currentRouteId(): string | null {
  const last = router?.state.matches.at(-1);
  const routeId = last?.routeId;
  return routeId && routeId !== ROOT_ROUTE_ID ? routeId : null;
}

function trackCurrentPageView(): void {
  if (!router) return;
  const href = router.state.location.href;
  const routeId = currentRouteId();
  if (!routeId || href === lastPageViewHref) return;
  lastPageViewHref = href;
  track("page_view", { route: routeId });
}

/**
 * Sends a `page_view` per navigation with the matched route pattern (`/boxes/$boxId`), the way
 * Sentry's router integration names its transactions: the pattern, never the path, so a box id
 * or a query string (`?session_id=`) is never recorded. A search-only change is not a page view.
 * The landing view of a store whose consent arrives later is sent when it does.
 */
export function initAnalytics(nextRouter: AnyRouter): () => void {
  router = nextRouter;
  const unsubscribe = nextRouter.subscribe(
    "onResolved",
    ({ fromLocation, toLocation, pathChanged }) => {
      if (fromLocation && !pathChanged) return;
      if (!enabled || toLocation.href === lastPageViewHref) return;
      const matches = nextRouter.matchRoutes(toLocation.pathname, toLocation.search, {
        throwOnError: false,
      });
      const routeId = matches.at(-1)?.routeId;
      if (!routeId || routeId === ROOT_ROUTE_ID) return;
      lastPageViewHref = toLocation.href;
      track("page_view", { route: routeId });
    },
  );
  if (!listenersInstalled) {
    listenersInstalled = true;
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") void flushAnalytics();
    });
    window.addEventListener("pagehide", () => void flushAnalytics());
  }
  return () => {
    unsubscribe();
    if (router === nextRouter) router = null;
  };
}

/**
 * The `code` an error carries as the catalog records it: a `LootErrorCode` from the API, else
 * `network_error` for a request that never got an answer, else `unknown`. Never a message.
 */
export function analyticsErrorCode(err: unknown): string {
  if (isLootApiErrorLike(err)) {
    const code = err.code.toLowerCase().replace(/[^a-z0-9_]/g, "_");
    if (/^[a-z][a-z0-9_]*$/.test(code)) return code.slice(0, 64);
    return "unknown";
  }
  if (err instanceof TypeError) return "network_error";
  return "unknown";
}
